<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import HelpIconWithModal from '@/corePagesModule/components/HelpIconWithModal.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import { ReadFileService } from '@/registerInputModule/service/ReadFileService';
import { computed, ref, watch } from 'vue';
import { assertNever } from '@/coreDomainsModule/assertNever';

const t = i18n.t;

const props = defineProps<{
  store: RegisterSettingStore;
}>();

const { state } = storeToRefs(props.store);

const emits = defineEmits<{
  (e: 'on-click-next'): void;
  (e: 'on-click-back'): void;
}>();

const localeSuffix = ref(i18n.locale === 'en' ? '__en' : '');

watch(
  () => i18n.locale,
  (newVal) => {
    localeSuffix.value = newVal === 'en' ? '__en' : '';
  },
  {
    immediate: true,
  }
);

const imagesRequire = require.context('@/assets/img', false, /^\.\/register-input.*\.png$/);

const localedImage = computed(() => {
  const registerInputDataImageSrc = './register-input-data-';
  const flowImageSrc = './register-input-step-flow-1' + localeSuffix.value + '.png';
  return {
    flowImage: imagesRequire(flowImageSrc),
    registerInputData: [...Array(2)].map((_, index) =>
      imagesRequire(registerInputDataImageSrc + (index + 1) + localeSuffix.value + '.png')
    ),
  };
});

/**
 * 戻るボタン
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

/**
 * 次へボタン
 */
const onClickNextButton = () => {
  emits('on-click-next');
};

const title = computed(() => {
  switch (state.value.uploadInput.inputFileType) {
    case '在庫':
      return t('入力アップロード：タイトル（在庫）');
    case '販売実績':
      return t('入力アップロード：タイトル（販売実績）');
    case '入荷':
      return t('入力アップロード：タイトル（入荷）');
    case '発注':
      return t('入力アップロード：タイトル（発注）');
    default:
      assertNever(state.value.uploadInput.inputFileType);
      return '';
  }
});

const type1Style = computed((): string => {
  return state.value.uploadInput.useFormatType === 'Inlined' ? 'data-type--selected' : 'data-type--not-selected';
});

const type2Style = computed((): string => {
  return state.value.uploadInput.useFormatType === 'InHeader' ? 'data-type--selected' : 'data-type--not-selected';
});
</script>

<template>
  <div class="upload-input">
    <div class="upload-input__head">{{ title }}</div>

    <img :src="localedImage.flowImage" class="upload-input__progress" />

    <template v-if="state.uploadInput.inputFileType !== '発注'">
      <div class="upload-input__title">{{ t('入力アップロード：データ形式選択') }}</div>

      <div class="upload-input__data-type data-type">
        <label for="data-type-1" class="data-type__type-1" :class="type1Style">
          <div class="data-type__top">
            <span class="data-type__title"> {{ t('入力アップロード：データ形式1：タイトル') }} </span>
            <input
              id="data-type-1"
              v-model="state.uploadInput.useFormatType"
              class="data-type__radio-button"
              type="radio"
              :value="'Inlined'"
            />
          </div>
          <span class="data-type__text"> {{ t('入力アップロード：データ形式1：タイトル：補足') }} </span>
          <img :src="localedImage.registerInputData[0]" class="data-type__image" />
        </label>

        <label for="data-type-2" class="data-type__type-2" :class="type2Style">
          <div class="data-type__top">
            <span class="data-type__title"> {{ t('入力アップロード：データ形式2：タイトル') }} </span>
            <input
              id="data-type-2"
              v-model="state.uploadInput.useFormatType"
              class="data-type__radio-button"
              type="radio"
              :value="'InHeader'"
            />
          </div>
          <span class="data-type__text"> {{ t('入力アップロード：データ形式2：タイトル：補足') }} </span>
          <img :src="localedImage.registerInputData[1]" class="data-type__image" />
        </label>
      </div>
    </template>

    <div class="upload-input__select-header select-header">
      <div class="select-header__text">
        <span class="select-header__text--note"> {{ t('入力アップロード：ヘッダー行指定') }}</span>
        <!-- STOPSHIP: ヘルプアイコンをfigma参考に挿入する -->
        <HelpIconWithModal :use-transform="true" class="select-header__text--help-icon">
          {{ t('') }}
        </HelpIconWithModal>
      </div>
      <div class="select-header__header-input header-input">
        <span class="header-input__label">{{ t('行番号') }}</span>
        <div class="header-input__input-text">
          <input
            v-model="state.uploadInput.headerNumber.value"
            class="header-input__input-text--form"
            type="number"
            :placeholder="t('半角数字')"
          />
          <span class="header-input__input-text--unit">{{ t('行') }}</span>
        </div>
        <span v-if="state.uploadInput.headerNumber.validateError == 'AtLeastOne'" class="header-input__input-text--error">{{
          t('取引設定：数値バリデーション：1以上エラー')
        }}</span>
        <span v-else-if="state.uploadInput.headerNumber.validateError == 'Empty'" class="header-input__input-text--error">{{
          t('取引設定：数値バリデーション：空文字エラー')
        }}</span>
        <span v-else class="header-input__input-text--error">{{ '　' }}</span>
      </div>
    </div>

    <div class="upload-input__upload-container">
      <div class="mb-20px dropzone" v-bind="store.getRootProps()">
        <input :data-testid="`dropzone_input_area`" v-bind="store.getInputProps()" />
        <div v-if="state.uploadInput.inputFile != null" :data-testid="`input_file_content`" class="dz-file-preview dz-preview">
          <div v-for="(file, index) in [state.uploadInput.inputFile]" :key="index" class="dz-file-content">
            <div class="dz-details">
              <div class="file-name-and-size">
                <span>{{ ReadFileService.fetchNameForEvent(file) }}</span>
                <span>{{ ReadFileService.getFileSizeStr(file) }}</span>
              </div>
              <span class="dz-remove" @click.stop="store.handleClickDeleteFile(index)"> Delete </span>
            </div>
          </div>
        </div>
        <div class="dz-default">
          <div class="dz-message" :class="store.isDragActive">
            <!-- STOPSHIP: 入力されたファイルのUIが微妙なので修正する -->
            <i />
            <p>{{ t('ここにファイルをドロップ') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="upload-input__button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
      <TextButton
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickNextButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：次へボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.upload-input {
  display: grid;
  gap: 12px;

  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  margin-top: 16px;
  padding: 16px;

  &__head {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__progress {
    justify-self: center;
  }

  &__upload-container {
    margin-top: 16px;
  }

  &__button-container {
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%));
    margin-top: 16px;
  }
}

.data-type {
  display: grid;
  gap: 16px;
  margin-top: 16px;
  grid-template-columns: repeat(2, auto);

  &__type-1 {
    border-radius: 4px;
    padding: 16px;
  }

  &__type-2 {
    border-radius: 4px;
    padding: 16px;
  }

  &__top {
    margin-bottom: 8px;
    display: grid;
    gap: 8px;
    grid-template-columns: auto auto;
  }

  &__radio-button {
    justify-self: end;
    height: 24px;
    width: 24px;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    line-height: 28px;
    color: $textDefault;
  }

  &__text {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: $textSecondary;
  }

  &__image {
    margin-top: 24px;
  }

  &--selected {
    border: 1px solid $semanticPrimary;
    background-color: $semanticPrimarySubdued;
  }

  &--not-selected {
    border: 1px solid $borderDefault;
    background-color: $semanticWhite;
  }
}

.select-header {
  display: grid;
  gap: 4px;
  grid-template-rows: repeat(2, fit-content(100%));
  margin-top: 16px;
  &__text {
    display: grid;
    gap: 4px;
    grid-template-columns: repeat(2, fit-content(100%));

    &--note {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      color: $textDefault;
    }

    &--help-icon {
      padding-top: 3px; // なんか表示がずれるので調整
      margin-left: 2px;
    }
  }

  &__header-input {
    margin-top: 16px;
  }
}

.header-input {
  display: grid;
  gap: 8px;
  grid-template-rows: repeat(3, fit-content(100%));

  &__label {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
  }

  &__input-text {
    &--form {
      border: none;
      outline: none;
      border-bottom: 1px solid $borderDefault;
      padding: 6px 8px;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }
  }
}
</style>
